import React,{ useEffect,useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getManualStepByPath,type ManualStep } from '../../utils/manualData';
import { ManualContext } from './ManualContext.shared';

export const ManualProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [overrideStepId, setOverrideStepId] = useState<number | null>(null);
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState<ManualStep | null>(null);

    // Update step based on location whenever it changes, unless overridden
    useEffect(() => {
        // If we are navigating, we usually want to reset the override unless explicitly kept.
        // For now, let's reset override on route change to be safe and transparent.
        setOverrideStepId(null);

        const step = getManualStepByPath(location.pathname);
        setCurrentStep(step);
    }, [location.pathname]);

    // Effect to handle override logic
    useEffect(() => {
        if (overrideStepId !== null) {
            // Need to import basic getter to avoid circular dependency issues if put inside useEffect?
            // Reuse the logic from utils but we know the IDs.
            // We need to import getManualStepById dynamically or just rely on the step lookup
            // We'll rely on the logic that manualData is static.
            import('../../utils/manualData').then(({ getManualStepById }) => {
                const step = getManualStepById(overrideStepId);
                setCurrentStep(step);
            });
        } else {
            // Fallback to location-based if override is cleared
            const step = getManualStepByPath(location.pathname);
            setCurrentStep(step);
        }
    }, [overrideStepId, location.pathname]);

    const openManual = () => setIsOpen(true);
    const closeManual = () => setIsOpen(false);
    const toggleManual = () => setIsOpen(prev => !prev);
    const setManualStepId = (id: number | null) => setOverrideStepId(id);

    return (
        <ManualContext.Provider value={{ isOpen, openManual, closeManual, toggleManual, currentStep, setManualStepId }}>
            {children}
        </ManualContext.Provider>
    );
};
